/**
 * Notifications API — the store behind the topbar bell.
 *
 * Rows carry no text. `type` + `params` come from the server and are rendered
 * through the i18n catalogs (see components/layout/NotificationBell.tsx): this
 * UI ships in six languages, and storing English sentences server-side would
 * freeze that.
 */

import { api } from "./client";

export type NotificationSeverity = "info" | "warning" | "critical";

export interface AppNotification {
  id: string;
  business_id: string;
  outlet_id?: string;
  /** e.g. "stock.low" — the i18n key is derived from this. */
  type: string;
  severity: NotificationSeverity;
  dedupe_key: string;
  params: Record<string, unknown>;
  subject_type?: string;
  subject_id?: string;
  /** In-app route to open when the row is clicked. */
  link?: string;
  required_perm?: string;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  read_at?: string;
  dismissed_at?: string;
}

export interface NotificationList {
  notifications: AppNotification[];
  unread_count: number;
}

export interface NotificationCount {
  /**
   * Absent means the server could not compute the count — not zero. A mutation
   * that succeeded but whose follow-up count query failed omits it rather than
   * reporting 0, so callers must keep the badge they have and refetch instead of
   * defaulting to 0.
   */
  unread_count?: number;
}

export const notificationsApi = {
  /**
   * `status`: "open" (default — neither dismissed nor auto-resolved), "unread",
   * or "all" for the history view.
   */
  list(params?: {
    status?: "open" | "unread" | "all";
    outlet_id?: string;
    limit?: number;
  }): Promise<NotificationList> {
    return api.get<NotificationList>("/notifications", params);
  },

  /** Just the badge. Polled far more often than the list is opened. */
  unreadCount(): Promise<NotificationCount> {
    return api.get<NotificationCount>("/notifications/unread-count");
  },

  markRead(id: string): Promise<NotificationCount> {
    return api.post<NotificationCount>(`/notifications/${id}/read`);
  },

  markAllRead(): Promise<NotificationCount> {
    return api.post<NotificationCount>("/notifications/read-all");
  },

  /** Clears a row the owner has dealt with. It will not come back until the
   *  underlying condition clears and happens again. */
  dismiss(id: string): Promise<NotificationCount> {
    return api.post<NotificationCount>(`/notifications/${id}/dismiss`);
  },

  dismissAll(): Promise<NotificationCount> {
    return api.post<NotificationCount>("/notifications/dismiss-all");
  },
};
